import { HeadlineTiny, RichTextContent } from "../../content/components";
import { StaticSiteDataContext } from "../../contexts/StaticSiteDataContext";
import { useVisibleGuestList } from "../../contexts/VisibleGuestListContext";
import { isEmptySlateDocument } from "../../core/constants";
import { AddToCalendar } from "../../events/components/AddToCalendar";
import { formatAddress, getEventDateTimeComponent } from "../../events/utils";
import { EventDetail } from "../../events/website/EventDetail";
import { getUserTimezone } from "../../guest-management/forms/EventForm/utils";
import { countryNameByCode } from "../../guest-management/forms/constants";
import { Configuration, WebsiteEvent } from "../../resources/modelTypes";
import { ThemedRichText } from "./section-types/ThemedRichText";
import { css } from "@emotion/react";
import {
  ActionLink,
  MediumHeadline,
  SmallHeadline,
} from "@minted/fancyclothes";
import { styleUtils, theme } from "@minted/minted-components";
import React, { memo, useContext, useEffect } from "react";

const displayEventStyles = {
  attending: css`
    margin-top: ${theme.spacing.x4};
    margin-bottom: ${theme.spacing.x4};
  `,
  eventInfo: css`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: ${theme.spacing.x4};
    max-width: ${styleUtils.rem(780)};
    margin-left: auto;
    margin-right: auto;
    ${theme.media.greaterThan(theme.breakpoints.medium)(`
      flex-direction: row;
    `)};
  `,
  guestName: css`
    list-style-type: none;
  `,
  infoBlock: css`
    width: 100%;
  `,
  infoValue: css`
    margin-top: ${theme.spacing.x3};
  `,
  schedule: css`
    margin-left: auto;
    margin-right: auto;
    max-width: ${styleUtils.rem(620)};
  `,
  title: css`
    margin-top: ${theme.spacing.x8};
  `,
  wrapper: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.x6};
  `,
};

export type DisplayEventProps = {
  configuration: Configuration;
  event: WebsiteEvent;
};

const DisplayEvent = ({ event }: DisplayEventProps) => {
  const { isWeddingSite } = useContext(StaticSiteDataContext);
  const {
    guests,
    hasLoaded: hasLoadedGuestList,
    refresh,
  } = useVisibleGuestList();
  const isTimezoneDifferentThanBrowser = event.timezone !== getUserTimezone();
  // Only show timezone abbreviation if event timezone differs from browser timezone
  const displayTimezone = isTimezoneDifferentThanBrowser
    ? event.timezone
    : null;

  const formattedAddress = event.hasPhysicalAddress ? formatAddress(event) : "";
  const {
    address1,
    address2,
    allowMaybeRsvp,
    city,
    country,
    date,
    description,
    endDate,
    endTime,
    hasPhysicalAddress,
    hostedBy,
    showAttending,
    showMaybe,
    showNotAttending,
    showNotReplied,
    state,
    time,
    title,
    venue,
    zipCode,
  } = event;

  const attendingGuests = guests.filter((guest) => guest.rsvpStatus === "yes");
  const notAttendingGuests = guests.filter(
    (guest) => guest.rsvpStatus === "no",
  );
  const maybeGuests = guests.filter((guest) => guest.rsvpStatus === "maybe");
  const notRepliedGuests = guests.filter((guest) => guest.rsvpStatus == null);

  const attendingCount = attendingGuests
    .map((guest) => guest.count)
    .reduce((total, guestCount) => total + guestCount, 0);
  const notAttendingCount = notAttendingGuests
    .map((guest) => guest.count)
    .reduce((total, guestCount) => total + guestCount, 0);
  const maybeCount = maybeGuests
    .map((guest) => guest.count)
    .reduce((total, guestCount) => total + guestCount, 0);
  const notRepliedCount = notRepliedGuests
    .map((guest) => guest.count)
    .reduce((total, guestCount) => total + guestCount, 0);

  useEffect(() => {
    /**
     * First api call happens in the provider setting 'shouldRefresh' to false when complete
     * so this is not going hit the api call unless we explicitly invalidate the collection
     * after making changes in the "Invite Guests" tab and then going back to "Design Invite".
     */
    if (hasLoadedGuestList) {
      refresh();
    }
  }, [refresh, hasLoadedGuestList]);

  return isWeddingSite ? (
    <div>
      <div css={displayEventStyles.title}>
        <SmallHeadline>{title}</SmallHeadline>
      </div>
      <div>
        <EventDetail
          address={formattedAddress}
          country={hasPhysicalAddress ? country : null}
          date={date}
          endDate={endDate}
          endTime={endTime}
          time={time}
          timezone={displayTimezone}
          venue={venue}
        />

        {description && <ThemedRichText content={description} />}

        {hasPhysicalAddress && (
          <ActionLink
            target="_blank"
            text="Get Directions"
            url={"https://www.google.com/maps/dir/?api=1&destination=".concat(
              encodeURIComponent(formattedAddress),
            )}
          />
        )}
      </div>
    </div>
  ) : (
    <div css={displayEventStyles.wrapper}>
      <div css={displayEventStyles.eventInfo}>
        {hostedBy?.trim() && (
          <div css={displayEventStyles.infoBlock}>
            <HeadlineTiny>Hosted By</HeadlineTiny>
            <div css={displayEventStyles.infoValue}>{hostedBy}</div>
          </div>
        )}
        <div css={displayEventStyles.infoBlock}>
          <HeadlineTiny>Date</HeadlineTiny>
          <div css={displayEventStyles.infoValue}>
            {getEventDateTimeComponent(
              date,
              time,
              endDate,
              endTime,
              displayTimezone,
            )}
          </div>
        </div>
        {(venue || hasPhysicalAddress) && (
          <div css={displayEventStyles.infoBlock}>
            <HeadlineTiny>Address</HeadlineTiny>
            <div css={displayEventStyles.infoValue}>
              {venue && venue !== address1 && (
                <>
                  {venue}
                  <br />
                </>
              )}
              {hasPhysicalAddress && (
                <>
                  {address1 && (
                    <>
                      {address1}
                      {address2 ? `, ${address2}` : ""}
                      <br />
                    </>
                  )}
                  {city || state || zipCode
                    ? `${city || ""}, ${state || ""} ${zipCode || ""}`
                    : ""}
                  {country && country !== "US" && (
                    <>
                      <br />
                      {countryNameByCode[country]}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
      {event.timezone && <AddToCalendar calendarUrls={event.calendarUrls} />}
      {!isEmptySlateDocument(description) && (
        <div>
          <MediumHeadline>Event Schedule</MediumHeadline>
          <div css={displayEventStyles.schedule}>
            <RichTextContent value={description} />
          </div>
        </div>
      )}
      {hasLoadedGuestList &&
        (showAttending || showNotAttending || showNotReplied || showMaybe) && (
          <div>
            <MediumHeadline>Guest List</MediumHeadline>
            {showAttending && (
              <>
                <div css={displayEventStyles.attending}>
                  {`${attendingCount} ATTENDING`}
                </div>
                {attendingGuests.map((guest, index) => (
                  <li
                    css={displayEventStyles.guestName}
                    key={`attending-${index}}`}
                  >
                    {guest.name} {guest.count > 1 && `(${guest.count})`}
                  </li>
                ))}
              </>
            )}
            {showMaybe && allowMaybeRsvp && (
              <>
                <div css={displayEventStyles.attending}>
                  {`${maybeCount} MAYBE`}
                </div>
                {maybeGuests.map((guest, index) => (
                  <li
                    css={displayEventStyles.guestName}
                    key={`maybe-${index}}`}
                  >
                    {guest.name} {guest.count > 1 && `(${guest.count})`}
                  </li>
                ))}
              </>
            )}
            {showNotAttending && (
              <>
                <div css={displayEventStyles.attending}>
                  {`${notAttendingCount} NOT ATTENDING`}
                </div>
                {notAttendingGuests.map((guest, index) => (
                  <li
                    css={displayEventStyles.guestName}
                    key={`notAttending-${index}}`}
                  >
                    {guest.name} {guest.count > 1 && `(${guest.count})`}
                  </li>
                ))}
              </>
            )}
            {showNotReplied && notRepliedGuests.length > 0 && (
              <>
                <div css={displayEventStyles.attending}>
                  {`${notRepliedCount} NOT YET REPLIED`}
                </div>
                {notRepliedGuests.map((guest, index) => (
                  <li
                    css={displayEventStyles.guestName}
                    key={`not-replied-${index}}`}
                  >
                    {guest.name} {guest.count > 1 && `(${guest.count})`}
                  </li>
                ))}
              </>
            )}
          </div>
        )}
    </div>
  );
};

const MemoizedDisplayEvent = memo(DisplayEvent);

export { MemoizedDisplayEvent as DisplayEvent };
